import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * AudienceChooser — first-visit "golfer or professional?" splash on the
 * apex homepage (/).
 *
 * The apex serves one SPA to two very different audiences. This overlay
 * asks new anonymous visitors which they are and routes pros to /pro so
 * each sees the audience-correct marketing page from the first second.
 *
 * Visually it reuses the marketing "Pick your side" panels: it renders
 * inside HomePage's `.mkt` wrapper, so the `.split` / `.apanel` design
 * system from marketingRedesign.css applies directly. The overlay itself
 * scrolls (overflow-y:auto with vertical padding) so the two full-height
 * panels always fit, even on a short mobile viewport.
 *
 * Why an overlay and not a hard route gate:
 *   - SEO: `/` stays the prerendered golfer marketing page. This overlay
 *     is client-only (reads localStorage in an effect, renders nothing
 *     server-side), so crawlers still index the full homepage beneath it.
 *   - Returning visitors: the choice is remembered in localStorage, so
 *     nobody is re-prompted on every load.
 *   - Escape hatch: dismissing defaults to the golfer view (the larger
 *     audience and the page they're already on).
 *
 * Gating is the caller's job (HomePage): only mount this for anonymous
 * web visitors once auth has resolved, never for signed-in users, the
 * native Pro shell, PWA launches, or auth-callback landings. This
 * component then layers a first-visit check on top of that.
 *
 * Lives entirely in the marketing app — no change to the Core or Pro
 * product apps.
 */
const STORAGE_KEY = 'golfauth_audience_choice'; // 'golfer' | 'pro'

export default function AudienceChooser() {
  const navigate = useNavigate();
  // Start hidden; decide on mount so we never flash the overlay during
  // prerender/hydration or for visitors who already chose.
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Never show during the Playwright marketing prerender (or e2e): the
    // snapshot runs with empty localStorage and would otherwise bake the
    // overlay — and its body scroll-lock — into the static HTML that ships
    // to every visitor. navigator.webdriver is true only in automated
    // browsers, false for every real user.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      // Private-mode / storage-blocked: show it once for this session
      // rather than gambling on persistence.
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const id = window.requestAnimationFrame(() => setMounted(true));
    // Prevent the page behind from scrolling while the splash is up.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.cancelAnimationFrame(id);
      document.body.style.overflow = prevOverflow;
    };
  }, [visible]);

  const choose = (choice: 'golfer' | 'pro') => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      /* non-fatal — worst case they see the splash again next visit */
    }
    if (choice === 'pro') {
      navigate('/pro');
      return;
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose your GolfAuth experience"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        overflowY: 'auto',
        backgroundColor: 'rgba(0, 6, 15, 0.94)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      {/* Scroll container: min-height 100% centres the card on tall
          screens and lets it scroll (with breathing room top and bottom)
          when the two full-height panels exceed a short mobile viewport. */}
      <div
        style={{
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 16px calc(24px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div
          className={`reveal ${mounted ? 'in' : ''}`}
          style={{ width: '100%', maxWidth: '900px' }}
        >
          <div style={{ textAlign: 'center', marginBottom: '26px' }}>
            <p className="eyebrow" style={{ marginBottom: '12px' }}>
              Welcome to GolfAuth
            </p>
            <h2
              style={{
                fontFamily: 'var(--display)',
                fontWeight: 800,
                letterSpacing: '-.025em',
                lineHeight: 1.02,
                fontSize: 'clamp(1.9rem, 5vw, 2.8rem)',
                color: 'var(--ink)',
              }}
            >
              Pick your side.
            </h2>
            <p
              style={{
                margin: '12px auto 0',
                maxWidth: '46ch',
                color: 'var(--body)',
                fontSize: '.98rem',
                lineHeight: 1.5,
              }}
            >
              GolfAuth serves golfers and the professionals who fit them.
              Choose one and we will take you to the right place.
            </p>
          </div>

          <div className="split">
            <button
              type="button"
              onClick={() => choose('golfer')}
              className="apanel g"
              style={{ textAlign: 'left', font: 'inherit', cursor: 'pointer' }}
            >
              <div className="a-bg" aria-hidden="true">
                <img src="/marketing/panel-golfer.jpg" alt="" className="media-cover" />
              </div>
              <span className="aglow" aria-hidden="true" />
              <span className="atag">For golfers</span>
              <h3>Know your bag.</h3>
              <p>Value it, play smarter with an AI caddie, and prove it is yours.</p>
              <span className="go">
                Explore for golfers
                <ArrowRight aria-hidden="true" />
              </span>
            </button>

            <button
              type="button"
              onClick={() => choose('pro')}
              className="apanel p"
              style={{ textAlign: 'left', font: 'inherit', cursor: 'pointer' }}
            >
              <div className="a-bg" aria-hidden="true">
                <img src="/marketing/panel-pro.jpg" alt="" className="media-cover" />
              </div>
              <span className="aglow" aria-hidden="true" />
              <span className="atag">For professionals</span>
              <h3>Run the shop.</h3>
              <p>
                Fitting to order to cash, get discovered by local golfers, and
                protect trade-in margin.
              </p>
              <span className="go">
                Explore GolfAuth Pro
                <ArrowRight aria-hidden="true" />
              </span>
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              type="button"
              onClick={() => choose('golfer')}
              style={{
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '.9rem',
                color: 'var(--muted)',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
            >
              Just browsing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
